import * as XLSX from 'xlsx';
import {resolveVars} from '../contentGenerator/formulaUtils';

// ── Export helpers ───────────────────────────────────────────────────

/**
 * Download an array-of-objects as CSV or XLSX.
 * @param {Object[]} rows — flat objects, one per row
 * @param {string[]} headers — column order
 * @param {string} filename — without extension
 * @param {'csv'|'xlsx'} format
 */
export function downloadSheet(rows, headers, filename, format = 'xlsx') {
    const data = [headers, ...rows.map((row) => headers.map((h) => row[h] ?? ''))];
    const ws = XLSX.utils.aoa_to_sheet(data);

    // Auto-size columns
    ws['!cols'] = headers.map((h) => {
        const maxLen = Math.max(
            h.length,
            ...rows.map((row) => String(row[h] ?? '').length),
        );
        return {wch: Math.min(Math.max(maxLen, 10), 60)};
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Content');

    if (format === 'csv') {
        const csv = XLSX.utils.sheet_to_csv(ws);
        triggerDownload(csv, `${filename}.csv`, 'text/csv;charset=utf-8;');
    } else {
        XLSX.writeFile(wb, `${filename}.xlsx`);
    }
}

function triggerDownload(content, filename, mimeType) {
    const blob = new Blob([content], {type: mimeType});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// ── Generator export ─────────────────────────────────────────────────

/**
 * Export generated results from ContentGenerator.
 * @param {Object[]} products — selected products with results
 * @param {Object} resultsByProductId
 * @param {Function} resolveVarsFn — resolves {{var}} in text
 * @param {'csv'|'xlsx'} format
 */
export function exportGeneratedResults(products, resultsByProductId, resolveVarsFn, format = 'xlsx') {
    const productsWithResults = products.filter((p) => resultsByProductId[p.id]);
    if (!productsWithResults.length) return;

    const maxBulletCount = productsWithResults.reduce((count, p) => {
        return Math.max(count, resultsByProductId[p.id]?.bullets?.length || 0);
    }, 0);
    const maxFaqCount = productsWithResults.reduce((count, p) => {
        return Math.max(count, resultsByProductId[p.id]?.faqs?.length || 0);
    }, 0);

    const headers = [
        'name', 'sku', 'category',
        'title', 'description',
        ...Array.from({length: maxBulletCount}, (_, i) => [
            `bullet_${i + 1}_label`,
            `bullet_${i + 1}`,
        ]).flat(),
        ...Array.from({length: maxFaqCount}, (_, i) => [
            `faq_${i + 1}_question`,
            `faq_${i + 1}_answer`,
        ]).flat(),
    ];

    const rows = productsWithResults.map((p) => {
        const r = resultsByProductId[p.id];
        const row = {
            name: p.name || '',
            sku: p.sku || '',
            category: p.category || '',
            title: resolveVarsFn(r.title),
            description: resolveVarsFn(r.desc),
        };
        (r.bullets || []).forEach((b, i) => {
            row[`bullet_${i + 1}_label`] = b.field || '';
            row[`bullet_${i + 1}`] = resolveVarsFn(b.text);
        });
        (r.faqs || []).forEach((faq, i) => {
            row[`faq_${i + 1}_question`] = resolveVarsFn(faq.question || '');
            row[`faq_${i + 1}_answer`] = resolveVarsFn(faq.answer || '');
        });
        return row;
    });

    downloadSheet(rows, headers, 'generated-content', format);
}

// ── Review export ────────────────────────────────────────────────────

const getTitleField = (listing) => listing.fields?.find((f) => f.type === 'title');
const getDescriptionField = (listing) => listing.fields?.find((f) => f.type === 'description');
const getBulletFields = (listing) => listing.fields?.filter((f) => f.type === 'bullet') || [];
const getFaqFields = (listing) => listing.fields?.filter((f) => f.type === 'faq') || [];

function formatSubgroupKey(meta) {
    if (!meta?.uniqueVarNames?.length) return '';
    return meta.uniqueVarNames
        .map((name) => `${name}:${meta.valuesByName?.[name] || ''}`)
        .join('|');
}

/**
 * Export review listings.
 * @param {Object[]} listings
 * @param {'csv'|'xlsx'} format
 * @param {'all'|'approved'} scope
 * @param {Function} [getVarsForListing] — returns vars array for a listing
 */
export function exportReviewListings(listings, format = 'xlsx', scope = 'all', getVarsForListing) {
    const resolveForListing = (text, listing) => {
        if (!text || !text.includes('{{') || typeof getVarsForListing !== 'function') return text;
        const vars = getVarsForListing(listing);
        return vars?.length ? resolveVars(text, vars) : text;
    };
    const filtered = scope === 'approved'
        ? listings.filter((l) => l.fields?.some((f) => f.status === 'approved'))
        : listings;
    if (!filtered.length) return;

    const maxBulletCount = filtered.reduce((count, listing) => {
        return Math.max(count, getBulletFields(listing).length);
    }, 0);
    const maxFaqCount = filtered.reduce((count, listing) => {
        return Math.max(count, getFaqFields(listing).length);
    }, 0);

    const headers = [
        'name', 'sku', 'group', 'category',
        'title', 'title_subgroup',
        'description', 'description_subgroup',
        ...Array.from({length: maxBulletCount}, (_, i) => [
            `bullet_${i + 1}_label`,
            `bullet_${i + 1}`,
            `bullet_${i + 1}_subgroup`,
        ]).flat(),
        ...Array.from({length: maxFaqCount}, (_, i) => [
            `faq_${i + 1}_question`,
            `faq_${i + 1}_answer`,
            `faq_${i + 1}_subgroup`,
        ]).flat(),
    ];

    const rows = filtered.map((listing) => {
        const title = getTitleField(listing);
        const description = getDescriptionField(listing);
        const bullets = getBulletFields(listing);
        const faqs = getFaqFields(listing);

        // If scope is 'approved', only include approved field text
        const titleText = scope === 'approved' && title?.status !== 'approved' ? '' : (title?.text || '');
        const descText = scope === 'approved' && description?.status !== 'approved' ? '' : (description?.text || '');

        const row = {
            name: listing.name || '',
            sku: listing.sku || '',
            group: listing.superSku || '',
            category: listing.category || '',
            title: resolveForListing(titleText, listing),
            title_subgroup: formatSubgroupKey(title?.subgroupMeta),
            description: resolveForListing(descText, listing),
            description_subgroup: formatSubgroupKey(description?.subgroupMeta),
        };

        bullets.forEach((field, i) => {
            const bulletText = scope === 'approved' && field.status !== 'approved' ? '' : (field.text || '');
            row[`bullet_${i + 1}_label`] = field.label || '';
            row[`bullet_${i + 1}`] = resolveForListing(bulletText, listing);
            row[`bullet_${i + 1}_subgroup`] = formatSubgroupKey(field.subgroupMeta);
        });

        faqs.forEach((field, i) => {
            // FAQs follow the all-or-nothing rule on publish; for
            // exports we mirror it field-by-field — unapproved Q&As get
            // blank cells under 'approved' scope.
            const isApproved =
                scope !== 'approved' || field.status === 'approved';
            row[`faq_${i + 1}_question`] = isApproved
                ? resolveForListing(field.question || '', listing)
                : '';
            row[`faq_${i + 1}_answer`] = isApproved
                ? resolveForListing(field.text || '', listing)
                : '';
            row[`faq_${i + 1}_subgroup`] = formatSubgroupKey(field.subgroupMeta);
        });

        return row;
    });

    const tag = scope === 'approved' ? 'approved' : 'review';
    downloadSheet(rows, headers, `${tag}-listings`, format);
}

// ── Import ───────────────────────────────────────────────────────────

/**
 * Read a CSV or XLSX file and return an array of row objects.
 * @param {File} file
 * @returns {Promise<Object[]>} — array of { header: value } objects
 */
export async function importFile(file) {
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data, {type: 'array'});
    const ws = wb.Sheets[wb.SheetNames[0]];
    return XLSX.utils.sheet_to_json(ws, {defval: ''});
}
